import {
  AdapterInterface,
  AdapterCdoMceInterface,
  AdapterCdoLn1Interface,
  AdapterCdoSequentInterface,
  AdapterBacnetInterface,
  BacnetBankMapping,
  BacnetLiftMapping,
  BacnetLn2Mapping,
} from "../interfaces/Adapter.interface";
import {
  AdapterCdoModbusInterface,
  ModbusToLn2Entry,
  Ln2ToModbusEntry,
} from "../interfaces/AdapterCdoModbus.interface";
import { AdapterCdoOpcInterface } from "../interfaces/AdapterCdoOpc.interface";
import { AdapterCdoOpcUa } from "../interfaces/AdapterCdoOpcUa.interface";
import {
  AdapterCdoLobbyVision2,
  BLANK_LOBBYVISION2_ADDRESSING,
} from "../interfaces/AdapterCdoLobbyVision2.interface";
import { EMPTY_UUID } from "../utils/uuid";
import { AdapterType } from "../flatbuffers/generated/archive/adapter-type";
import { AdapterProtocol } from "../flatbuffers/generated/archive/adapter-protocol";
import { AdapterAuth } from "../flatbuffers/generated/archive/adapter-auth";
import { LiftType } from "../flatbuffers/generated/archive/lift-type";
import { VendorFloorIndexing } from "../flatbuffers/generated/archive/vendor-floor-indexing";
import { SecurityPolicy } from "../flatbuffers/generated/opc-ua/security-policy";
import { MessageSecurityMode } from "../flatbuffers/generated/opc-ua/message-security-mode";

/**
 * Protocols whose verified status requires CDO content validation.
 * Used by the setup wizard to distinguish adapters that need more than
 * just a name to be considered configured.
 */
export const CDO_PROTOS = new Set([
  AdapterProtocol[AdapterProtocol.MCE],
  AdapterProtocol[AdapterProtocol.LN1],
  AdapterProtocol[AdapterProtocol.RVU],
  AdapterProtocol[AdapterProtocol.CGA],
  AdapterProtocol[AdapterProtocol.LOBBYVISION],
]);

export const BLANK_ADAPTER_CDO_MCE: AdapterCdoMceInterface = {
  idl_file: "",
  product_type: 0,
  event: {
    mapping_file: "",
    mapping_data: "",
  },
};

export const BLANK_ADAPTER_CDO_MODBUS: AdapterCdoModbusInterface = {
  slave_id: 0,
  polling_interval: 1000,
  ln2_to_msg_map: [],
  msg_to_ln2_map: [],
};

export const BLANK_ADAPTER_CDO_OPC: AdapterCdoOpcInterface = {
  opc_server: "",
  hostname: "",
  location: {
    name: "",
    sites: [],
  },
  sites: {
    opc_cmd_to_msg: [],
    msg_to_opc_cmd: [],
    opc_cmd_to_sequence: [],
  },
  locations: {
    opc_cmd_to_msg: [],
    msg_to_opc_cmd: [],
    opc_cmd_to_sequence: [],
  },
  car_num_to_name: [],
  groups: {
    mappings: {
      opc_cmd_to_msg: [],
      msg_to_opc_cmd: [],
      opc_cmd_to_sequence: [],
    },
    path_only: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
    data_only: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
    path_and_data: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
  },
  lifts: {
    mappings: {
      opc_cmd_to_msg: [],
      msg_to_opc_cmd: [],
      opc_cmd_to_sequence: [],
    },
    path_only: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
    data_only: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
    path_and_data: {
      mappings: {
        opc_cmd_to_msg: [],
        msg_to_opc_cmd: [],
        opc_cmd_to_sequence: [],
      },
    },
  },
};

/** Blank OPC UA adapter CDO form state. See `AdapterCdoOpcUa.interface.ts` for field docs. */
export const BLANK_ADAPTER_CDO_OPCUA: AdapterCdoOpcUa = {
  applicationName: "",
  applicationUri: "",
  productUri: "",
  securityPolicy: SecurityPolicy.None_,
  securityMode: MessageSecurityMode.None_,
  identity: { kind: "anonymous" },
  certificate: null,
  session: null,
  redundancy: null,
  subscription: null,
  namedSubscriptions: [],
  monitors: [],
  nodeToMsg: [],
  msgToNode: [],
  methods: [],
  eventMonitors: [],
  eventMappings: [],
  historyReads: [],
};

/** Blank LobbyVision 2 adapter CDO form state. See `AdapterCdoLobbyVision2.interface.ts` for field docs. */
export const BLANK_ADAPTER_CDO_LOBBYVISION2: AdapterCdoLobbyVision2 = {
  reads: [],
  writes: [],
  addressing: { ...BLANK_LOBBYVISION2_ADDRESSING },
};

export const DEFAULT_BACNET_LIFT_MAPPING: BacnetLiftMapping = {
  bacnet_lift_id: EMPTY_UUID,
  bacnet_lift_name: "",
  ln2_lift_id: EMPTY_UUID,
  ln2_lift_name: "",
};

export const DEFAULT_BACNET_BANK_MAPPING: BacnetBankMapping = {
  bacnet_bank_id: EMPTY_UUID,
  bacnet_bank_name: "",
  ln2_bank_id: EMPTY_UUID,
  ln2_bank_name: "",
  lift_mappings: [],
};

export const BLANK_ADAPTER_CDO_BACNET: AdapterBacnetInterface = {
  entries: [],
};

export const BLANK_ADAPTER_CDO_SEQUENT: AdapterCdoSequentInterface = {
  discrete_2_message: [],
  dev_type: 0,
};

export const BLANK_ADAPTER_CDO_LN1: AdapterCdoLn1Interface = {
  terminating_zeroes_expected: {
    from_device_serial: 1,
    refresh_all: 1,
    set_one_bit: 1,
    reinitialize_all_devices: 1,
    set_one_byte: 1,
    rvu: 1,
    rvu_heartbeat: 1,
  },
  crc_override: false,
  should_filter_duplicates: true,
  allowed_messages: null,
  heartbeat_kind: null,
};

export const BLANK_ADAPTER: AdapterInterface = {
  id: null,
  leadingZeroesId: null,
  name: "CEC-Swift-3C16F",
  version: "1.0.0",
  type: AdapterType.WEBSOCKET,
  protocolType: AdapterProtocol.MCE,
  authType: AdapterAuth.PASSWORD,
  liftType: LiftType.ELEVATOR,
  active: true,
  vendorFloorIndexing: VendorFloorIndexing.ONE_BASED,
  interfaceJson: null,
  offsetMappingJson: null,
  dictionaryJson: null,
  cdoInputJson: null,
  cdoTemplateJson: null,
  socketMessagePriorityJson: null,
  bacnetConfigJson: null,
  bacnetBankMappingsJson: null,
  intraContentFlagsJson: null,
  cdoSchemaName: null,
  cdoSchemaVersion: 1,
  defaultFaultsConfiguration: true,
  defaultIconDefinitionConfiguration: true,
  faultsConfigShowCounter: false,
  faultsConfigDelaySeconds: 0,
  faultsConfigRulesJson: null,
  totalBacnetChannels: 0,
  createdAt: "2024-04-22T20:08:33.235602Z",
  updatedAt: "2025-02-18T19:19:02.146561Z",
  deletedAt: null,
  // Optional nested relations
  keys: [],
  actionCatalogs: [],
  bacnetConfig: [],
  bacnetBankMappings: [],
  cdoInput: BLANK_ADAPTER_CDO_MCE,
  faultsConfiguration: { showCounter: true, rules: [] },
  floors: 1,
  protocolId: "",
  authCredentials: {
    username: "",
    password: "",
  },
};

export const DEFAULT_BACNET_LN2_MAPPING: BacnetLn2Mapping = {
  point: {
    host_name: "",
    point_name: "",
    group_name: "",
    host_id: "",
    group_id: "",
    value: 0,
    point_id: 0,
    bank_name: "",
    bank_id: "",
    lift_id: "",
    lift_name: "",
  },
  ln2_msg: {
    bank_id: "",
    lift_id: "",
    msg: {
      encoded_type: 0,
      message_content: 0,
      message_value: 0,
      fbs_device_type: LiftType.ELEVATOR,
      position: null,
      active: true,
    },
  },
};

export const DEFAULT_MODBUS_MSG_TO_LN2: ModbusToLn2Entry = {
  key_2: 0,
  key_1: {
    message: {
      kind: "ReadCoilsResponse",
      data: [],
    },
  },
  val: {
    msg: {
      msg: { content: {} },
      active: false,
    },
    bank_name: "",
    lift_name: "",
    bank_id: "",
    lift_id: "",
  },
};

export const DEFAULT_MODBUS_LN2_TO_MSG: Ln2ToModbusEntry = {
  key: {
    msg: {
      msg: { content: {} },
      active: false,
    },
    bank_name: "",
    lift_name: "",
    bank_id: "",
    lift_id: "",
  },
  val: { message: { kind: "ReadCoilsRequest", address: 0, quantity: 0 } },
};
